import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useAuth } from '../../hooks/auth/use_auth'
import FlatButton from '../../components/buttons/flat_button'
import Logo from '../../images/logo.png'
import MainBackground from '../../images/main_bg.png'
import CopyrightFooter from '../../components/layouts/copyright_footer'

interface LoginFormData {
  email: string
  password: string
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const { login, isAuthenticated } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>()

  // 既にログイン済みの場合はトップページにリダイレクト
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  const onSubmit = async (data: LoginFormData) => {
    setIsLoggingIn(true)
    setLoginError(null)

    try {
      const isSuccess = await login(data)
      if (isSuccess) {
        navigate('/dashboard')
      } else {
        setLoginError(
          'ログインに失敗しました。メールアドレスとパスワードを確認してください。'
        )
      }
    } catch (error) {
      setLoginError(
        'エラーが発生しました。しばらくしてから再度お試しください。'
      )
    } finally {
      setIsLoggingIn(false)
    }
  }

  const handleTogglePassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <div
      className="min-h-screen flex flex-col justify-center items-center bg-cover bg-center bg-no-repeat p-4 relative"
      style={{
        backgroundImage: `url(${MainBackground})`,
      }}
    >
      <div className="bg-white rounded-xl py-4 px-4 flex flex-col justify-center items-center max-w-xl md:w-2/3">
        {/* ロゴエリア */}
        <div className="mb-4 flex items-center justify-center">
          <Link to="/">
            <img
              src={Logo}
              alt="SDGs Lodge Logo"
              width={300}
              height={100}
              className="mt-8"
            />
          </Link>
        </div>

        <div className="w-full max-w-md">
          <div className="p-4">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* メールアドレス */}
              <div>
                {errors.email && (
                  <p className="text-main-red text-sm mb-1">
                    {errors.email.message}
                  </p>
                )}
                <input
                  type="email"
                  placeholder="メールアドレス"
                  {...register('email', {
                    required: 'メールアドレスは必須です',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: '有効なメールアドレスを入力してください',
                    },
                  })}
                  className="w-full py-2 px-4 border border-black rounded-md outline-none"
                />
              </div>

              {/* パスワード */}
              <div>
                <div className="relative">
                  {errors.password && (
                    <p className="text-main-red text-sm mb-1">
                      {errors.password.message}
                    </p>
                  )}
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="パスワード"
                      {...register('password', {
                        required: 'パスワードは必須です',
                        minLength: {
                          value: 6,
                          message: 'パスワードは6文字以上で入力してください',
                        },
                      })}
                      className="w-full py-2 px-4 border border-black rounded-md outline-none"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={handleTogglePassword}
                    >
                      <span className="material-icons text-main-darkgray hover:text-main-darkgray">
                        {showPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* エラーメッセージ */}
              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                  <p className="text-sm">{loginError}</p>
                </div>
              )}

              {/* ログインボタン */}
              <FlatButton
                type="submit"
                className="w-full"
                disabled={isLoggingIn}
              >
                {isLoggingIn ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    ログイン中...
                  </div>
                ) : (
                  'ログイン'
                )}
              </FlatButton>

              {/* ローディングインジケーター */}
              {isLoggingIn && (
                <div className="flex items-center justify-center text-main-green">
                  <div className="animate-pulse">
                    <p className="text-sm">認証処理を実行しています...</p>
                  </div>
                </div>
              )}
            </form>

            {/* パスワードリセットリンク */}
            <div className="text-center mt-6">
              <Link
                to="/password/reset/request"
                className="text-sm text-main-green hover:text-main-green hover:underline"
              >
                パスワードをお忘れの方はこちら
              </Link>
            </div>
            <div className="text-center mt-2">
              <Link
                to="/register/mail/send"
                className="text-sm text-main-green hover:text-main-green hover:underline"
              >
                会員登録はこちら
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* フッター */}
      <CopyrightFooter />
    </div>
  )
}

export default LoginPage
